/**
 * CreateTransferBatch.jsx
 * =======================
 * Receipt-linked transfer flow (Phase: Receipt-First Transfers).
 *
 * Flow
 * ----
 * Agency staff:
 *  1. Sees a table of receipts that are CONFIRMED + PAID + UNBATCHED
 *     (i.e. `status ∈ {created, validated}` AND `transfer_batch_id == null`).
 *  2. Picks the receipts they actually want to transfer (some may stay in the
 *     agency on purpose: refundable tickets, pending files, follow-ups, etc).
 *  3. The total is computed automatically from the selection.
 *  4. Submits → backend creates a TransferBatch and LOCKS the selected entries
 *     by setting `transfer_batch_id` on each (atomic, server-side).
 *
 * Accountant flow (existing — untouched):
 *  - Cannot pick receipts.
 *  - Sees the batch in the existing transfer-audit-trail screen with full
 *    receipt details (client, employee, original op, date, status).
 *  - Clicks "تأكيد" / "رفض"
 *  - On reject/cancel, the backend automatically returns every entry to
 *    `status=validated` and clears the batch link → available again.
 *
 * No existing route, page, or behaviour is altered by this component.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  new Intl.NumberFormat('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(n || 0));

const STATUS_LABEL = {
  created: { text: 'مُنشأ', color: 'bg-gray-100 text-gray-700' },
  validated: { text: 'موثّق', color: 'bg-emerald-100 text-emerald-700' },
  transferred: { text: 'مُحوَّل', color: 'bg-blue-100 text-blue-700' },
  received: { text: 'مُستلَم', color: 'bg-indigo-100 text-indigo-700' },
  cancelled: { text: 'ملغى', color: 'bg-red-100 text-red-700' },
};

export default function CreateTransferBatch() {
  const token = localStorage.getItem('token');
  const authHeader = { Authorization: `Bearer ${token}` };

  const [user, setUser] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState('');

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // selection: Set of entry ids
  const [selected, setSelected] = useState(new Set());

  // submission form
  const [recipientName, setRecipientName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load user + agencies once
  useEffect(() => {
    (async () => {
      try {
        const u = await axios.get(`${API}/auth/me`, { headers: authHeader });
        setUser(u.data);
        // For staff: own agency only. For accountant/super_admin: dropdown.
        if (u.data.role === 'agency_staff') {
          setAgencyId(u.data.agency_id);
        } else {
          const ags = await axios.get(`${API}/agencies`, { headers: authHeader });
          setAgencies(ags.data || []);
          setAgencyId(u.data.agency_id || (ags.data?.[0]?.id ?? ''));
        }
      } catch (e) {
        toast.error('فشل تحميل بيانات المستخدم');
      }
    })();
  }, []);

  // Load available entries when agencyId changes
  const loadEntries = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/financial/entries`, {
        headers: authHeader,
        params: {
          agency_id: agencyId,
          unbatched_only: true,
          per_page: 500,
        },
      });
      setEntries(r.data?.items || []);
      setSelected(new Set());  // reset selection on reload
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل تحميل الوصولات');
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Filter by search
  const visibleEntries = useMemo(() => {
    if (!search.trim()) return entries;
    const s = search.trim().toLowerCase();
    return entries.filter((e) =>
      [e.receipt_no, e.client_name, e.description, e.employee_name]
        .filter(Boolean)
        .some((f) => f.toLowerCase().includes(s))
    );
  }, [entries, search]);

  // Selection helpers
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === visibleEntries.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleEntries.map((e) => e.id)));
    }
  };

  const selectedEntries = useMemo(
    () => entries.filter((e) => selected.has(e.id)),
    [entries, selected]
  );
  const selectedTotal = useMemo(
    () => selectedEntries.reduce((sum, e) => sum + Number(e.amount || 0), 0),
    [selectedEntries]
  );

  const submit = async () => {
    if (selected.size === 0) {
      toast.error('اختر وصلاً واحداً على الأقل');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/financial/batches`,
        {
          entry_ids: Array.from(selected),
          recipient_name: recipientName || null,
          notes: notes || null,
          confirm_now: false,  // staff creates an OPEN batch; accountant confirms later
        },
        { headers: authHeader }
      );
      toast.success(
        `تم إنشاء تحويل ${r.data.reference_no} بقيمة ${fmt(r.data.total_amount)} دج. ` +
        'سيُرسَل للمحاسب للتأكيد.'
      );
      // Reset & reload
      setSelected(new Set());
      setRecipientName('');
      setNotes('');
      await loadEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل إنشاء التحويل');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" dir="rtl" data-testid="create-transfer-batch-page">
      <header className="border-b pb-4">
        <h1 className="text-2xl font-bold text-gray-900">
          💰 إنشاء تحويل من الوصولات
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          اختر الوصولات التي تريد تحويلها للمحاسبة العامة. الوصولات التي لا تختارها
          تبقى في الوكالة (تذاكر قابلة للاسترجاع، دفعات تحت المتابعة، إلخ).
        </p>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-gray-600">الوصولات المتاحة</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{entries.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-gray-600">إجمالي المتاح</div>
            <div className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
              {fmt(entries.reduce((s, e) => s + Number(e.amount || 0), 0))} دج
            </div>
          </CardContent>
        </Card>
        <Card className={selected.size > 0 ? 'ring-2 ring-brand-500 bg-brand-50' : ''}>
          <CardContent className="p-4">
            <div className="text-xs text-gray-600">الوصولات المختارة</div>
            <div className="text-2xl font-bold text-brand-700 mt-1">{selected.size}</div>
          </CardContent>
        </Card>
        <Card className={selected.size > 0 ? 'ring-2 ring-brand-500 bg-brand-50' : ''}>
          <CardContent className="p-4">
            <div className="text-xs text-gray-600">إجمالي التحويل</div>
            <div className="text-2xl font-bold text-brand-700 mt-1 tabular-nums">
              {fmt(selectedTotal)} دج
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            {user && user.role !== 'agency_staff' && (
              <div>
                <Label htmlFor="agency">الوكالة</Label>
                <select
                  id="agency"
                  value={agencyId}
                  onChange={(e) => setAgencyId(e.target.value)}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background"
                  data-testid="ctb-agency-select"
                >
                  {agencies.map((a) => (
                    <option key={a.id} value={a.id}>{a.name} - {a.city}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label htmlFor="ctb-search">بحث (رقم الوصل / العميل / الوصف)</Label>
              <Input
                id="ctb-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ابحث..."
                data-testid="ctb-search-input"
              />
            </div>
            <Button variant="outline" onClick={loadEntries} disabled={loading} data-testid="ctb-refresh-btn">
              🔄 تحديث القائمة
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Entries table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>📋 الوصولات المتاحة للتحويل</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleAll}
              disabled={visibleEntries.length === 0}
              data-testid="ctb-toggle-all-btn"
            >
              {selected.size === visibleEntries.length && visibleEntries.length > 0
                ? '❌ إلغاء تحديد الكل'
                : '✅ تحديد كل المعروض'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">جاري التحميل…</div>
          ) : visibleEntries.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {entries.length === 0
                ? 'لا توجد وصولات متاحة للتحويل في هذه الوكالة.'
                : 'لا توجد نتائج تطابق البحث.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 w-12 text-center">
                      <Checkbox
                        checked={selected.size === visibleEntries.length && visibleEntries.length > 0}
                        onCheckedChange={toggleAll}
                        data-testid="ctb-header-checkbox"
                      />
                    </th>
                    <th className="px-3 py-3 text-right">رقم الوصل</th>
                    <th className="px-3 py-3 text-right">التاريخ</th>
                    <th className="px-3 py-3 text-right">العميل</th>
                    <th className="px-3 py-3 text-right">الموظف</th>
                    <th className="px-3 py-3 text-right">نوع العملية</th>
                    <th className="px-3 py-3 text-right">طريقة الدفع</th>
                    <th className="px-3 py-3 text-right">الحالة</th>
                    <th className="px-3 py-3 text-right">المبلغ (دج)</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEntries.map((e) => {
                    const isSelected = selected.has(e.id);
                    const st = STATUS_LABEL[e.status] || { text: e.status, color: 'bg-gray-100 text-gray-700' };
                    return (
                      <tr
                        key={e.id}
                        className={`border-b cursor-pointer ${isSelected ? 'bg-brand-50' : 'hover:bg-gray-50'}`}
                        onClick={() => toggleOne(e.id)}
                        data-testid={`ctb-row-${e.id}`}
                      >
                        <td className="px-3 py-3 text-center" onClick={(ev) => ev.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleOne(e.id)}
                            data-testid={`ctb-check-${e.id}`}
                          />
                        </td>
                        <td className="px-3 py-3 font-mono text-xs">{e.receipt_no}</td>
                        <td className="px-3 py-3 text-gray-600 text-xs">
                          {e.created_at ? new Date(e.created_at).toLocaleDateString('fr-DZ') : '-'}
                        </td>
                        <td className="px-3 py-3 font-medium">{e.client_name || '-'}</td>
                        <td className="px-3 py-3 text-gray-700">{e.employee_name || '-'}</td>
                        <td className="px-3 py-3">{e.operation_type || '-'}</td>
                        <td className="px-3 py-3">{e.payment_method || '-'}</td>
                        <td className="px-3 py-3">
                          <Badge className={st.color}>{st.text}</Badge>
                        </td>
                        <td className="px-3 py-3 text-left font-bold tabular-nums">
                          {fmt(e.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submission panel (sticky) */}
      {selected.size > 0 && (
        <Card className="sticky bottom-4 shadow-2xl border-2 border-brand-500" data-testid="ctb-submit-panel">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="recipient">اسم المُستلِم (اختياري)</Label>
                <Input
                  id="recipient"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="مثال: المحاسب العام"
                  data-testid="ctb-recipient-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="notes">ملاحظات (اختياري)</Label>
                <Textarea
                  id="notes"
                  rows={1}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="أي ملاحظة للمحاسب..."
                  data-testid="ctb-notes-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="text-sm text-gray-600">
                ستُرسَل <strong>{selected.size}</strong> وصلاً بقيمة إجمالية{' '}
                <strong className="text-brand-700">{fmt(selectedTotal)} دج</strong>{' '}
                للمحاسب للتأكيد.
              </div>
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="ctb-submit-btn"
              >
                {submitting ? 'جاري الإرسال…' : '📤 إنشاء التحويل وإرساله للمحاسب'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
